"use client"

import React, { ReactNode, useCallback, useEffect, useRef } from "react"
import clsx from "clsx"
import { twMerge } from "tailwind-merge"

import { usePathname } from "@bullseye/navigation"

import { IconButton } from "../IconButton"
import { Box } from "../Layout"
import { P } from "../Typography"

export type SheetProps = {
  renderContent: (show: boolean) => ReactNode
  renderFooter?: (show: boolean) => ReactNode
  show: boolean
  title?: ReactNode
  icon?: "x" | "arrow-left"
  iconVariant?: "plain" | "filled"
  onIconPress?: () => void
  dismiss?: () => void
  size?: "md" | "lg"
  contentContainerStyle?: string
  footerContainerStyle?: string
  disableSafeArea?: boolean
  sheetDragDisabled?: boolean
}

const ANIMATION_DURATION = 300

const emptyFooter = () => <></>

export const Sheet = ({
  renderContent,
  renderFooter = emptyFooter,
  size = "md",
  show,
  title,
  icon,
  iconVariant = "plain",
  dismiss,
  onIconPress,
  contentContainerStyle = "",
  footerContainerStyle = "",
}: SheetProps) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const isClosing = useRef(false)

  const dismissWithAnimations = useCallback(() => {
    if (!dismiss || !show || isClosing.current) return

    const panel = panelRef.current
    const overlay = overlayRef.current
    const dialog = dialogRef.current
    if (!dialog || !panel || !overlay) {
      dismiss()
      return
    }

    isClosing.current = true
    panel.style.transform = "translateY(100%)"
    overlay.style.opacity = "0"

    const onEnd = () => {
      panel.removeEventListener("transitionend", onEnd)
      isClosing.current = false
      if (dialog.open) dialog.close()
      dismiss()
    }
    panel.addEventListener("transitionend", onEnd, { once: true })
  }, [dismiss, show])

  const pathname = usePathname()
  useEffect(() => {
    dismissWithAnimations()
  }, [pathname])

  useEffect(() => {
    const dialog = dialogRef.current
    const panel = panelRef.current
    const overlay = overlayRef.current
    if (!dialog || !panel || !overlay) return

    if (show && !dialog.open) {
      isClosing.current = false
      panel.style.transform = "translateY(100%)"
      overlay.style.opacity = "0"
      dialog.showModal()
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          panel.style.transform = "translateY(0)"
          overlay.style.opacity = "1"
        })
      })
    } else if (!show && dialog.open) {
      isClosing.current = false
      dialog.close()
    }
  }, [show])

  const handleCancel = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault()
      dismissWithAnimations()
    },
    [dismissWithAnimations],
  )

  return (
    <dialog
      ref={dialogRef}
      onCancel={handleCancel}
      aria-label={typeof title === "string" ? title : undefined}
      style={{
        position: "fixed",
        inset: 0,
        margin: 0,
        width: "100%",
        height: "100%",
        maxWidth: "100%",
        maxHeight: "100%",
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        overflow: "hidden",
      }}
    >
      {/* Backdrop overlay */}
      <div
        ref={overlayRef}
        onClick={dismissWithAnimations}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          opacity: 0,
          transition: `opacity ${ANIMATION_DURATION}ms ease`,
        }}
      />

      {/* Centering container */}
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) dismissWithAnimations()
        }}
        style={{ pointerEvents: "none" }}
        className={clsx(
          "relative flex h-full w-full",
          "items-end md:items-center md:justify-center md:py-4",
        )}
      >
        {/* Panel */}
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            pointerEvents: "auto",
            transform: "translateY(100%)",
            transition: `transform ${ANIMATION_DURATION}ms ease`,
          }}
          className={clsx(
            "flex-col flex w-full cursor-default bg-white p-4 drop-shadow g-4 md:w-[576px] md:rounded-2xl",
            {
              "h-full": size === "lg",
              "rounded-t-2xl": size !== "lg",
            },
          )}
        >
          {(title || icon) && (
            <Box
              className={clsx(
                "flex-0 flex flex-row items-center justify-center",
              )}
            >
              {icon && (
                <IconButton
                  name="close"
                  handlePress={() => {
                    if (icon === "x") {
                      dismissWithAnimations()
                    } else {
                      onIconPress?.()
                    }
                  }}
                  iconType={icon}
                  variant={iconVariant}
                  hoverColor="bg-white"
                  className="flex-grow-0"
                />
              )}
              <Box className="flex-[2] justify-self-center">
                {title && (
                  <P bold className="text-center !font-semibold">
                    {title}
                  </P>
                )}
              </Box>
              {icon && title && <Box className="flex-grow-1 w-[32px]" />}
            </Box>
          )}
          <Box
            className={twMerge(
              clsx({
                "flex-1": size === "lg",
                "pb-5": size === "lg",
              }),
              contentContainerStyle,
            )}
          >
            {renderContent(show)}
          </Box>
          <Box
            className={twMerge(
              "flex-0 justify-center",
              footerContainerStyle,
            )}
          >
            {renderFooter(show)}
          </Box>
        </div>
      </div>
    </dialog>
  )
}
